import React, { useState, useEffect, useReducer, useContext } from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Chip from "@material-ui/core/Chip";
import Typography from "@material-ui/core/Typography";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import CompanyModal from "../../components/CompaniesModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { useDate } from "../../hooks/useDate";
import moment from "moment";

const reducer = (state, action) => {
  if (action.type === "LOAD_COMPANIES") {
    const companies = action.payload;
    const newCompanies = [];
    companies.forEach((company) => {
      const idx = state.findIndex((u) => u.id === company.id);
      if (idx !== -1) state[idx] = company;
      else newCompanies.push(company);
    });
    return [...state, ...newCompanies];
  }
  if (action.type === "UPDATE_COMPANIES") {
    const company = action.payload;
    const idx = state.findIndex((u) => u.id === company.id);
    if (idx !== -1) { state[idx] = company; return [...state]; }
    return [company, ...state];
  }
  if (action.type === "DELETE_COMPANIES") {
    const id = action.payload;
    const idx = state.findIndex((u) => u.id === id);
    if (idx !== -1) state.splice(idx, 1);
    return [...state];
  }
  if (action.type === "RESET") return [];
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  /* desktop table */
  tableHead: {
    "& th": {
      fontWeight: 700,
      fontSize: 11,
      textTransform: "uppercase",
      letterSpacing: "0.05em",
      color: theme.palette.text.secondary,
      whiteSpace: "nowrap",
      padding: "8px 12px",
    },
  },
  /* mobile card list */
  cardList: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
    padding: "8px 4px 16px",
  },
  card: {
    background: "#fff",
    borderRadius: 12,
    padding: "12px 14px",
    display: "flex",
    flexDirection: "column",
    gap: 8,
    border: "1px solid rgba(0,0,0,0.08)",
  },
  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 8,
  },
  cardName: {
    fontWeight: 700,
    fontSize: 15,
    color: "#1e293b",
    lineHeight: 1.3,
  },
  cardId: {
    fontSize: 11,
    color: "#94a3b8",
    fontWeight: 500,
    marginTop: 2,
  },
  cardRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: "6px 16px",
  },
  cardField: {
    display: "flex",
    flexDirection: "column",
    minWidth: 80,
  },
  cardLabel: {
    fontSize: 10,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    color: "#94a3b8",
    marginBottom: 1,
  },
  cardValue: {
    fontSize: 13,
    color: "#334155",
    fontWeight: 500,
  },
  divider: {
    height: 1,
    background: "rgba(0,0,0,0.06)",
    margin: "2px 0",
  },
}));

const dueDateStyle = (dueDate) => {
  if (!moment(dueDate).isValid()) return {};
  const diff = moment(dueDate).diff(moment(), "days");
  if (diff >= 1 && diff <= 5) return { borderLeft: "4px solid #f59e0b", background: "#fffbeb" };
  if (diff <= 0) return { borderLeft: "4px solid #ef4444", background: "#fef2f2" };
  return {};
};

const statusChip = (status) => (
  <Chip
    label={status === false ? "Inactivo" : "Activo"}
    size="small"
    style={{
      backgroundColor: status === false ? "rgba(239,68,68,0.12)" : "rgba(34,197,94,0.12)",
      color: status === false ? "#dc2626" : "#16a34a",
      fontWeight: 700,
      fontSize: 11,
      height: 20,
    }}
  />
);

const formatSize = (size) => {
  if (!size || size === 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let i = 0, s = size;
  while (s >= 1024 && i < units.length - 1) { s /= 1024; i++; }
  return `${s.toFixed(1)} ${units[i]}`;
};

const recurrenceMap = {
  monthly: "Mensual", mensal: "Mensual",
  bimonthly: "Bimestral", bimestral: "Bimestral",
  quarterly: "Trimestral", trimestral: "Trimestral",
  semester: "Semestral", semestral: "Semestral",
  yearly: "Anual", anual: "Anual",
};

const Companies = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("xs"));
  const history = useHistory();

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [deletingCompany, setDeletingCompany] = useState(null);
  const [companyModalOpen, setCompanyModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [companies, dispatch] = useReducer(reducer, []);
  const { dateToClient, datetimeToClient } = useDate();
  const { user } = useContext(AuthContext);

  useEffect(() => {
    if (!user.super) {
      toast.error("Sin permiso para acceder a esta página.");
      setTimeout(() => history.push("/"), 1000);
    }
  }, []);

  useEffect(() => { dispatch({ type: "RESET" }); setPageNumber(1); }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get("/companiesPlan/", { params: { searchParam, pageNumber } });
        dispatch({ type: "LOAD_COMPANIES", payload: data.companies });
        setHasMore(data.hasMore);
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchParam, pageNumber]);

  const handleDeleteCompany = async (companyId) => {
    try {
      await api.delete(`/companies/${companyId}`);
      toast.success(i18n.t("compaies.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingCompany(null);
    setSearchParam("");
    setPageNumber(1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) setPageNumber((p) => p + 1);
  };

  const planValue = (company) =>
    company.planId !== null && company.plan?.amount
      ? company.plan.amount.toLocaleString("es-MX", { style: "currency", currency: "MXN" })
      : "—";

  const lastUpdate = (company) => {
    const lu = company?.metrics?.lastUpdate;
    return lu ? datetimeToClient(lu) : "No disponible";
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={deletingCompany && `${i18n.t("compaies.confirmationModal.deleteTitle")} ${deletingCompany.name}?`}
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDeleteCompany(deletingCompany.id)}
      >
        {i18n.t("compaies.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <CompanyModal
        open={companyModalOpen}
        onClose={() => { setSelectedCompany(null); setCompanyModalOpen(false); }}
        aria-labelledby="form-dialog-title"
        companyId={selectedCompany?.id}
      />

      <MainHeader>
        <Title>{i18n.t("compaies.title")} ({companies.length})</Title>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined" onScroll={handleScroll}>
        {isMobile ? (
          /* ── MOBILE: tarjetas ── */
          <div className={classes.cardList}>
            {companies.map((company) => (
              <div
                key={company.id}
                className={classes.card}
                style={dueDateStyle(company.dueDate)}
              >
                {/* Header */}
                <div className={classes.cardHeader}>
                  <div>
                    <div className={classes.cardName}>{company.name}</div>
                    <div className={classes.cardId}>ID {company.id} · {company.email}</div>
                  </div>
                  {statusChip(company.status)}
                </div>

                <div className={classes.divider} />

                {/* Plan + valor */}
                <div className={classes.cardRow}>
                  <div className={classes.cardField}>
                    <span className={classes.cardLabel}>{i18n.t("compaies.table.namePlan")}</span>
                    <span className={classes.cardValue}>{company?.plan?.name || "—"}</span>
                  </div>
                  <div className={classes.cardField}>
                    <span className={classes.cardLabel}>{i18n.t("compaies.table.value")}</span>
                    <span className={classes.cardValue} style={{ fontWeight: 700 }}>{planValue(company)}</span>
                  </div>
                </div>

                {/* Fechas */}
                <div className={classes.cardRow}>
                  <div className={classes.cardField}>
                    <span className={classes.cardLabel}>{i18n.t("compaies.table.createdAt")}</span>
                    <span className={classes.cardValue}>{dateToClient(company.createdAt)}</span>
                  </div>
                  <div className={classes.cardField}>
                    <span className={classes.cardLabel}>{i18n.t("compaies.table.dueDate")}</span>
                    <span className={classes.cardValue}>
                      {dateToClient(company.dueDate)}
                      {company.recurrence && (
                        <span style={{ color: "#64748b", fontSize: 11, marginLeft: 4 }}>
                          · {recurrenceMap[company.recurrence] || company.recurrence}
                        </span>
                      )}
                    </span>
                  </div>
                  <div className={classes.cardField}>
                    <span className={classes.cardLabel}>{i18n.t("compaies.table.lastLogin")}</span>
                    <span className={classes.cardValue}>{datetimeToClient(company.lastLogin)}</span>
                  </div>
                </div>

              </div>
            ))}
            {loading && (
              <Typography align="center" color="textSecondary" style={{ padding: 16 }}>
                Cargando...
              </Typography>
            )}
          </div>
        ) : (
          /* ── DESKTOP: tabla ── */
          <Table size="small">
            <TableHead className={classes.tableHead}>
              <TableRow>
                <TableCell align="center">{i18n.t("compaies.table.ID")}</TableCell>
                <TableCell align="center">{i18n.t("compaies.table.status")}</TableCell>
                <TableCell align="center">{i18n.t("compaies.table.name")}</TableCell>
                <TableCell align="center">{i18n.t("compaies.table.email")}</TableCell>
                <TableCell align="center">{i18n.t("compaies.table.namePlan")}</TableCell>
                <TableCell align="center">{i18n.t("compaies.table.value")}</TableCell>
                <TableCell align="center">{i18n.t("compaies.table.createdAt")}</TableCell>
                <TableCell align="center">{i18n.t("compaies.table.dueDate")}</TableCell>
                <TableCell align="center">{i18n.t("compaies.table.lastLogin")}</TableCell>
                <TableCell align="center">{i18n.t("compaies.table.folderSize").trim()}</TableCell>
                <TableCell align="center">{i18n.t("compaies.table.totalFiles").trim()}</TableCell>
                <TableCell align="center">{i18n.t("compaies.table.lastUpdate")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <>
                {companies.map((company) => {
                  const ds = dueDateStyle(company.dueDate);
                  const isExpired = ds.borderLeft?.includes("ef4444");
                  const textColor = isExpired ? "#991b1b" : undefined;
                  return (
                    <TableRow key={company.id} style={ds} hover>
                      <TableCell align="center" style={{ color: textColor }}>{company.id}</TableCell>
                      <TableCell align="center">{statusChip(company.status)}</TableCell>
                      <TableCell align="center" style={{ color: textColor, fontWeight: 600 }}>{company.name}</TableCell>
                      <TableCell align="center" style={{ color: textColor }}>{company.email}</TableCell>
                      <TableCell align="center" style={{ color: textColor }}>{company?.plan?.name || "—"}</TableCell>
                      <TableCell align="center" style={{ fontWeight: 700, color: textColor }}>{planValue(company)}</TableCell>
                      <TableCell align="center" style={{ color: textColor }}>{dateToClient(company.createdAt)}</TableCell>
                      <TableCell align="center" style={{ color: textColor }}>
                        {dateToClient(company.dueDate)}
                        {company.recurrence && (
                          <div style={{ fontSize: 11, color: isExpired ? "#ef4444" : "#64748b" }}>
                            {recurrenceMap[company.recurrence] || company.recurrence}
                          </div>
                        )}
                      </TableCell>
                      <TableCell align="center" style={{ color: textColor }}>{datetimeToClient(company.lastLogin)}</TableCell>
                      <TableCell align="center" style={{ color: textColor }}>{formatSize(company?.metrics?.folderSize || 0)}</TableCell>
                      <TableCell align="center" style={{ color: textColor }}>{company?.metrics?.numberOfFiles || 0}</TableCell>
                      <TableCell align="center" style={{ color: textColor }}>{lastUpdate(company)}</TableCell>
                    </TableRow>
                  );
                })}
                {loading && <TableRowSkeleton columns={12} />}
              </>
            </TableBody>
          </Table>
        )}
      </Paper>
    </MainContainer>
  );
};

export default Companies;
